import type { ReactNode } from 'react';
import {
  Briefcase,
  GraduationCap,
  MapPin,
  Link2,
  UserSearch,
  ExternalLink,
} from 'lucide-react';
import type { FishFullContact } from '@Types/fish';

type EnrichedProfileCardProps = {
  data: FishFullContact;
};

/** Staff-facing brand for the enrichment source; the vendor is not named in the UI. */
const ENRICHMENT_BRAND = 'Profile+';

/**
 * Whether a FullContact enrichment has any renderable content. Lets a parent
 * decide whether to show the card (e.g. as a swiper slide) without rendering it.
 */
export const hasEnrichmentContent = (data?: FishFullContact | null): boolean => {
  const details = data?.details;
  if (!details) return false;
  const location = details.locations?.[0];
  const hasIdentity = Boolean(details.age?.range || details.gender || location);
  const hasSocial = Object.values(details.profiles ?? {}).some((p) => p?.url);
  return Boolean(
    hasIdentity ||
      details.employment?.length ||
      details.education?.length ||
      hasSocial ||
      details.interests?.length,
  );
};

const formatEnrichedDate = (raw: string): string => {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const Section = ({
  icon,
  title,
  wide = false,
  children,
}: {
  icon: ReactNode;
  title: string;
  /** Spans both grid columns in light mode. */
  wide?: boolean;
  children: ReactNode;
}) => (
  <div
    className={`flex flex-col gap-1.5 rounded-xl bg-white p-3 shadow-xs dark:gap-1 dark:rounded-none dark:bg-transparent dark:p-0 dark:shadow-none ${
      wide ? 'sm:col-span-2 dark:sm:col-span-1' : ''
    }`}
  >
    <div className="flex items-center gap-2 text-[11px] font-bold tracking-wider text-purple-700 uppercase dark:gap-1.5 dark:text-[10px] dark:text-purple-300">
      {icon}
      {title}
    </div>
    <div className="text-[15px]/snug text-aoh-ink dark:text-sm dark:text-zinc-200">
      {children}
    </div>
  </div>
);

/**
 * Renders the FullContact enrichment stored at
 * fishProfile.additionalMeta.fullContact. Every section is optional — the
 * FullContact response is sparse, so only populated data is shown.
 */
const EnrichedProfileCard = ({ data }: EnrichedProfileCardProps) => {
  const details = data?.details;
  if (!details) return null;

  const location = details.locations?.[0];
  const locationLabel =
    location?.formatted ||
    [location?.city, location?.regionCode || location?.region, location?.country]
      .filter(Boolean)
      .join(', ');

  const identityBits = [
    details.age?.range ? `Age ${details.age.range}` : null,
    details.gender || null,
    locationLabel || null,
  ].filter(Boolean);

  const socialLinks = Object.entries(details.profiles ?? {})
    .map(([service, profile]) => ({ service, url: profile?.url }))
    .filter((p): p is { service: string; url: string } => Boolean(p.url));

  if (!hasEnrichmentContent(data)) return null;

  const enrichedOn = data.enrichedDate ? formatEnrichedDate(data.enrichedDate) : '';

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-purple-200 bg-purple-50 p-4 dark:rounded-lg dark:border-purple-900/60 dark:bg-purple-950/20 dark:shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-purple-600 text-white dark:h-8 dark:w-8 dark:rounded-md dark:bg-purple-500 dark:shadow-sm">
            <UserSearch className="h-5 w-5 dark:h-4 dark:w-4" />
          </div>
          <div className="flex flex-col">
            <span className="text-base leading-tight font-bold text-aoh-ink dark:text-[10px] dark:leading-normal dark:tracking-wider dark:text-purple-300 dark:uppercase">
              Enriched profile
            </span>
            <span className="text-[12px] text-aoh-muted dark:text-[10px] dark:text-zinc-400">
              via <span className="font-semibold">{ENRICHMENT_BRAND}</span>
              {enrichedOn && (
                <span className="dark:hidden"> · Updated {enrichedOn}</span>
              )}
            </span>
          </div>
        </div>
        {enrichedOn && (
          <span className="hidden text-[10px] whitespace-nowrap text-zinc-400 dark:inline">
            Updated {enrichedOn}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 dark:gap-4">
        {identityBits.length > 0 && (
          <Section icon={<MapPin className="h-4 w-4 dark:h-3 dark:w-3" />} title="Identity">
            {identityBits.join(' · ')}
          </Section>
        )}

        {details.employment?.length ? (
          <Section icon={<Briefcase className="h-4 w-4 dark:h-3 dark:w-3" />} title="Employment">
            <ul className="flex flex-col gap-0.5">
              {details.employment.map((job, i) => (
                <li key={i}>
                  {[job.title, job.name].filter(Boolean).join(' at ')}
                  {job.current ? (
                    <span className="ml-1 text-[12px] text-green-700 dark:text-[10px] dark:text-green-400">
                      (current)
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        {details.education?.length ? (
          <Section
            icon={<GraduationCap className="h-4 w-4 dark:h-3 dark:w-3" />}
            title="Education"
            wide
          >
            <ul className="flex flex-col gap-0.5">
              {details.education.map((edu, i) => (
                <li key={i}>
                  {[edu.degree, edu.name].filter(Boolean).join(', ')}
                  {edu.end?.year ? (
                    <span className="ml-1 text-aoh-muted dark:text-zinc-400">
                      ({edu.end.year})
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        {details.interests?.length ? (
          <Section icon={<Link2 className="h-4 w-4 dark:h-3 dark:w-3" />} title="Interests" wide>
            {details.interests
              .map((interest) => interest.name)
              .filter(Boolean)
              .join(', ')}
          </Section>
        ) : null}
      </div>

      {socialLinks.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {socialLinks.map(({ service, url }) => (
            <a
              key={service}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 rounded-full border border-purple-200 bg-white px-2.5 py-1 text-xs font-medium text-purple-700 capitalize transition-colors hover:bg-purple-100 dark:rounded-md dark:px-2 dark:font-normal dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-200 dark:hover:bg-purple-900/50"
            >
              {service}
              <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default EnrichedProfileCard;
